import React, { useState } from 'react';
import logger from 'utils/logger';

import intl from 'react-intl-universal';
import Grid from '@mui/material/Grid2';
import { validatePassword } from 'utils/misc';
import { Box, Typography, Button, Stack } from '@mui/material';
import pxToRem from 'assets/theme/functions/pxToRem';
import { getCurrentUser, updatePassword } from 'aws-amplify/auth';
import { ConfirmModal } from 'shared/modals/confirmModal';
import PasswordInput from './Password';

const PASSWORD_TYPES = {
  CURRENT: 'currentPassword',
  NEW: 'newPassword',
  CONFIRM: 'confirmNewPassword',
};

const PASSWORD_ERRORS = {
  NOT_AUTHORIZED: 'NotAuthorizedException',
  INVALID_PASSWORD: 'InvalidPasswordException',
  NETWORK_ERROR: 'NetworkError',
  LIMIT_EXCEEDED: 'LimitExceededException',
};

// Create a logger instance for this component
const log = logger.createLogger('SecurityTab');

const SecurityTab = () => {
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [passwordChangeStatus, setPasswordChangeStatus] = useState({
    show: false,
    success: false,
    errorCode: '',
  });
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [showErrorOnSubmit, setShowErrorOnSubmit] = useState(false);

  const resetInputs = () => {
    setPasswords({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    });
    setErrors({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    });
    setShowErrorOnSubmit(false);
  };

  const handlePasswordInput = (key) => async (event) => {
    const newValue = event.target.value;
    setPasswords({ ...passwords, [key]: newValue });

    if (showErrorOnSubmit) {
      // Clear errors for the current field and confirm password if it's new password
      const newErrors = { ...errors };
      newErrors[key] = '';
      if (key === PASSWORD_TYPES.NEW) {
        newErrors.confirmNewPassword = '';
      }
      setErrors(newErrors);

      // If all errors are cleared, reset showErrorOnSubmit
      if (Object.values(newErrors).every((error) => error === '')) {
        setShowErrorOnSubmit(false);
      }

      // Only validate if we have both new and confirm passwords
      if (key === PASSWORD_TYPES.NEW && passwords.confirmNewPassword) {
        validatePasswords();
      } else if (key === PASSWORD_TYPES.CONFIRM && passwords.newPassword) {
        validatePasswords();
      }
    }
  };

  const validatePasswords = () => {
    const newErrors = {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    };

    if (passwords.currentPassword === '') {
      newErrors.currentPassword = intl.get(
        'personal_settings_password_warn_text_empty_input'
      );
    }

    if (passwords.newPassword === '') {
      newErrors.newPassword = intl.get(
        'personal_settings_password_warn_text_empty_input'
      );
    } else if (!validatePassword(passwords.newPassword)) {
      newErrors.newPassword = intl.get(
        'personal_settings_password_requirements_message_2'
      );
    }

    if (passwords.confirmNewPassword === '') {
      newErrors.confirmNewPassword = intl.get(
        'personal_settings_password_warn_text_empty_input'
      );
    } else if (passwords.newPassword !== passwords.confirmNewPassword) {
      newErrors.confirmNewPassword = intl.get(
        'personal_settings_password_warn_text_inconsistent_input'
      );
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === '');
  };

  const handleClickUpdateButton = () => {
    setShowErrorOnSubmit(true);
    if (!validatePasswords()) {
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmUpdateButton = async () => {
    setShowConfirmModal(false);

    try {
      // Get the current authenticated user
      await getCurrentUser();

      // Update password using the new Amplify v6 method
      await updatePassword({
        oldPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });

      // Handle success
      setPasswordChangeStatus({
        show: true,
        success: true,
        errorCode: '',
      });
      resetInputs();
    } catch (err) {
      log.error('Error occurred', { error: err });
      setPasswordChangeStatus({
        show: true,
        success: false,
        errorCode: err.name, // Note: In v6, we use err.name instead of err.code
      });
    }
  };

  const passwordChangeFailWindowContents = () => {
    switch (passwordChangeStatus.errorCode) {
      case PASSWORD_ERRORS.NOT_AUTHORIZED:
        return (
          <Grid size={12}>
            <Typography>
              {intl.get(
                'personal_settings_password_error_not_authorized_title'
              )}
            </Typography>
            <Typography style={{ marginBottom: '10px' }}>
              {intl.get(
                'personal_settings_password_error_not_authorized_message_1'
              )}
            </Typography>
          </Grid>
        );

      case PASSWORD_ERRORS.NETWORK_ERROR:
        return (
          <Grid size={12}>
            <Typography style={{ marginBottom: '10px' }}>
              {intl.get(
                'personal_settings_password_error_network_error_message_1'
              )}
            </Typography>
          </Grid>
        );

      case PASSWORD_ERRORS.LIMIT_EXCEEDED:
        return (
          <Grid size={12}>
            <Typography style={{ marginBottom: '10px' }}>
              {intl.get(
                'personal_settings_password_error_limit_exceeded_message_1'
              )}
            </Typography>
            <Typography style={{ marginBottom: '10px' }}>
              {intl.get(
                'personal_settings_password_error_limit_exceeded_message_2'
              )}
            </Typography>
          </Grid>
        );

      default:
        return (
          <Grid size={12}>
            <Typography style={{ marginBottom: '10px' }}>
              {intl.get(
                'personal_settings_password_error_unknown_error_message_1'
              )}
            </Typography>
          </Grid>
        );
    }
  };

  return (
    <>
      <Stack
        spacing={5}
        sx={{
          margin: '32px auto 0',
          width: '100%',
          maxWidth: '500px',
        }}
        alignItems="center">
        <PasswordInput
          label={intl.get('personal_settings_password_current_password')}
          value={passwords.currentPassword}
          onChange={handlePasswordInput(PASSWORD_TYPES.CURRENT)}
          required
          error={showErrorOnSubmit && errors.currentPassword !== ''}
          helperText={showErrorOnSubmit ? errors.currentPassword : ''}
          fullWidth
        />

        <PasswordInput
          label={intl.get('personal_settings_password_new_password')}
          value={passwords.newPassword}
          onChange={handlePasswordInput(PASSWORD_TYPES.NEW)}
          required
          error={showErrorOnSubmit && errors.newPassword !== ''}
          helperText={
            showErrorOnSubmit && errors.newPassword
              ? errors.newPassword
              : intl.get('personal_settings_password_requirements_message_2')
          }
          helperTextStyle={
            showErrorOnSubmit && errors.newPassword !== '' ? 'error' : 'info'
          }
          fullWidth
        />

        <PasswordInput
          label={intl.get('personal_settings_password_confirm_new_password')}
          value={passwords.confirmNewPassword}
          onChange={handlePasswordInput(PASSWORD_TYPES.CONFIRM)}
          required
          error={showErrorOnSubmit && errors.confirmNewPassword !== ''}
          helperText={showErrorOnSubmit ? errors.confirmNewPassword : ''}
          fullWidth
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleClickUpdateButton}
          sx={{
            color: '#ffffff !important',
            fontSize: pxToRem(18),
            width: 'fit-content',
            minWidth: '200px',
          }}>
          {intl.get('personal_settings_button_update')}
        </Button>
      </Stack>

      <ConfirmModal
        isOpened={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmUpdateButton}
        title={intl.get('personal_settings_password_confirm_modal_title')}
        confirmButtonText={intl.get(
          'personal_settings_password_confirm_modal_button_yes'
        )}
        cancelButtonText={intl.get(
          'personal_settings_password_confirm_modal_button_no'
        )}
      />

      <ConfirmModal
        isOpened={passwordChangeStatus.show}
        onConfirm={() =>
          setPasswordChangeStatus({ ...passwordChangeStatus, show: false })
        }
        title={
          passwordChangeStatus.success
            ? undefined
            : intl.get('personal_settings_password_fail_message_1')
        }
        bodyText={
          passwordChangeStatus.success
            ? intl.get('personal_settings_password_success_message_1')
            : undefined
        }
        confirmButtonText={intl.get(
          'organization_settings_client_settings_modal_text_confirm_title'
        )}>
        {!passwordChangeStatus.success && (
          <Box>
            <Grid
              spacing={3}
              container
              justifyContent="flex-end"
              alignItems="flex-end">
              {passwordChangeFailWindowContents()}
            </Grid>
          </Box>
        )}
      </ConfirmModal>
    </>
  );
};

export default SecurityTab;
